import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { TransactionListItem } from '@/components/transactions/TransactionListItem';
import { TransactionReceiptModal } from '@/components/transactions/TransactionReceiptModal';
import { Loader } from '@/components/ui/Loader';
import { ROUTES } from '@/constants/routes';
import { userTransactionService, getApiErrorMessage } from '@/services';
import type { TransactionItem } from '@/types/transactions';
import { notify } from '@/utils/notify';

export function RecentTransactionsCard() {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<TransactionItem[]>([]);
  const [selected, setSelected] = useState<TransactionItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await userTransactionService.recent(5);
      setTransactions(res.data?.transactions ?? []);
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load recent transactions'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-soft sm:p-5">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-bold text-brand-navy">Recent transactions</h2>
            <p className="text-xs text-brand-muted">Your latest wallet activity</p>
          </div>
          <Link
            to={ROUTES.dashboardTransactions}
            className="inline-flex items-center gap-1 text-xs font-semibold text-brand-green hover:underline"
          >
            View all
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        {loading ? (
          <Loader label="Loading transactions..." />
        ) : transactions.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500">No transactions yet.</p>
        ) : (
          <div className="space-y-2">
            {transactions.map((tx) => (
              <TransactionListItem
                key={tx.id}
                transaction={tx}
                onClick={() => setSelected(tx)}
                className="shadow-none"
              />
            ))}
          </div>
        )}
      </div>

      <TransactionReceiptModal
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        transaction={selected}
      />
    </>
  );
}
